/** Copy dictionaries for the Vault settings section. */

/** English strings (the key-set source of truth for this pair). */
export const en = {
  nav: 'Vault',
  heading: 'Vault',
  intro: 'Encrypted credentials stored on this machine.',
  searchPlaceholder: 'Search title, username, email, host…',
  loading: 'Loading…',
  empty: 'No credentials yet. Add one to get started.',
  noFiltered: 'No entries match the current filter.',
  noFilteredHint: 'Try a different search or clear the filters.',
  clearFilters: 'Clear filters',
  emptyHint: 'Tip: you can ask the assistant to save an API key or token you share in chat — with auto-capture on it offers to store it for you. Bulk importing? Ask for vault_import_csv with a CSV file path.',
  emptyHintReadonly: 'The vault is in readonly mode — credentials can be read but not added here. Switch accessMode to "readwrite" to enable writes.',
  modeLabel: 'Access mode',
  autoCaptureLabel: 'Auto-capture credentials from chat',
  modeReadonly: 'Read-only',
  modeAsk: 'Ask before writing',
  modeAuto: 'Automatic read-write',
  modeReadonlyHint: 'Read-only — mutations disabled',
  modeAskHint: 'Prompt before writing — every add/update/delete asks you',
  modeAutoHint: 'Automatic read-write — no per-call prompt',
  autoCaptureOn: 'auto-capture on',
  autoCaptureOff: 'auto-capture off',
  clearSearch: 'Clear search',
  entryCount: 'Entries',
  reportTitle: 'Health & rotation',
  reportJumpHint: 'Open this entry',
  recentActivity: 'Recent activity',
  recentlyAdded: 'Recently added',
  reportRotation: 'expired / due for rotation',
  reportWeak: 'weak passwords',
  healthSummaryHint: 'Vault health: weak/reused/no-2FA/HTTP/rotation counts',
  reportReused: 'credentials reused across entries',
  dupGroups: 'duplicate groups',
  dupTitle: 'Duplicate entries',
  dupMerge: 'Merge',
  healthOk: 'vault healthy',
  sessionCollectHint: 'Collect the cookies from this browser window and save them as a session',
  vaultSwitchHint: 'Switch vault (applies to every tab)',
  vaultTargetLabel: 'Vault',
  vaultTargetHint: 'Pick the vault that the buttons below act on',
  vaultRenameHint: 'Rename the selected vault',
  vaultDeleteHint: 'Delete the selected vault (the default vault cannot be deleted)',
  vaultNewHint: 'Create a new empty vault with the same master password',
  toolProfileCustom: 'Custom…',
  toolGroupManagement: 'Management',
  toolGroupIo: 'Import / export',
  toolGroupSessions: 'Browser sessions',
  toolGroupFiles: 'Backups & vault files',
  toolProfile: 'Model tools',
  toolProfileHint: 'Basic keeps the model tool list small; switch to Standard/Full (or Custom) when a task needs more. Applies immediately.',
  toolProfileHintLong: 'Which vault tools the model may call. Basic = CRUD, search, generate, TOTP. Standard adds vault management (favorites, tags, expiry, health, duplicates, trash, attachments, backups). Full adds bulk import/export, browser sessions and vault-file operations. Custom = Basic + the groups you tick. Takes effect immediately; persisted per vault directory.',
  toolProfileBasic: 'Basic (recommended)',
  toolProfileStandard: 'Standard',
  toolProfileFull: 'Full',
  toolProfileApplied: 'Tool profile applied — {n} tools registered',
  previewShort: 'Preview',
  copySecretHint: 'Copy this entry\'s secret (password / key / token) — clipboard clears after 30s',
  sysSearchHint: 'Search Chrome and the macOS Keychain for this keyword (names only, no secrets)',
  exportCsvHint: 'Export the chosen fields of every entry to a CSV file (plaintext!)',
  tagRenameHint: 'Rename this tag across all entries',
  tagRemoveHint: 'Remove this tag from all entries',
  sessionImportHint: 'Paste exported cookies (JSON array or Cookie header) and save them as a session entry',
  auditQuickHint: 'Quick filter: all / reads / searches / changes',
  auditExportHint: 'Export the current audit view as a CSV file',
  auditLoadMoreHint: 'Show more audit events',
  importNameChrome: 'Chrome',
  importNameFirefox: 'Firefox',
  importNameKeychain: 'macOS Keychain',
  importName1password: '1Password',
  importNameBitwarden: 'Bitwarden',
  importNameBitwardenEncrypted: 'Bitwarden (encrypted)',
  importName1pif: '1Password 1PIF',
  importNameKeePassXml: 'KeePass XML',
  importNameKdbx: 'KeePass KDBX',
  importNameEnpass: 'Enpass',
  importNameManagerCsv: 'Manager CSV',
  importIntro: 'Pick a source, preview first (preview never writes), then import.',
  importRun: 'Import',
  importChromeShort: 'Passwords saved in this machine\'s Chrome',
  importFirefoxShort: 'Passwords from a local Firefox profile',
  importKeychainShort: 'macOS Keychain (login & generic passwords)',
  import1passwordShort: '1Password export file (.1pux)',
  importBitwardenShort: 'Bitwarden / Vaultwarden JSON export',
  importBitwardenEncryptedShort: 'Password-protected Bitwarden JSON',
  import1pifShort: 'Legacy 1Password export (.1pif)',
  importKeePassXmlShort: 'KeePass 2.x XML export',
  importKdbxShort: 'KeePass database file (.kdbx, 3.1/4.x)',
  importEnpassShort: 'Enpass JSON export',
  importManagerCsvShort: 'Any manager CSV (columns auto-detected)',
  grpChecks: 'Security checks',
  grpBackup: 'Backup',
  grpAccess: 'Access & recovery',
  exportReportHint: 'Export this health/security report (weak/reused/2FA/HTTP/rotation) as a CSV',
  breachHint: 'Check stored passwords against Have I Been Pwned (online; only SHA-1 hash prefixes leave this machine)',
  backupNowHint: 'Create an encrypted backup copy now (keeps the newest N, prunes older)',
  lockHint: 'Wipe the in-memory key and lock the vault (unlock needs the master password)',
  recoveryGenHint: 'Generate a one-time recovery code to prove ownership if the master password is lost',
  recoveryVerHint: 'Verify that you hold a previously generated recovery code',
  statBadgeHint: 'Vault statistics. Click a health badge (weak / reused / 2FA / HTTP / expiry) to filter the list to it.',
  badgeFilterHint: 'Click to filter the list to this problem; click again to clear',
  kindChipHint: 'Click to filter by this type; click again to clear',
  dueFilterHint: 'Click to show only expired / due entries',
  healthBackup: 'backup',
  backupNow: 'Backup now',
  backupDone: 'Backup created',
  lockedBanner: 'Vault is locked — unlock it with vault_unlock to read or write entries.',
  vaultSelect: 'Active vault',
  vaultRename: 'Rename vault',
  vaultRenamePrompt: 'New name for the vault',
  vaultDelete: 'Delete',
  vaultDeleteConfirm: 'Permanently delete vault "{name}"? This cannot be undone.',
  vaultDeleteDefault: 'The default vault cannot be deleted.',
  touch: 'Used',
  touchHint: 'Mark as recently used',
  auditTitle: 'Needs attention',
  auditEdit: 'Edit',
  breachCheck: 'Check breaches',
  exportReport: 'Export report',
  reportExported: 'Security report exported as CSV.',
  breachTitle: 'Breach scan',
  breachPwned: 'password appeared in breaches',
  breachWeak: 'common/weak password',
  breachOffline: 'offline fallback',
  breachOk: 'No breached or weak passwords found',
  breachOkOffline: 'No weak passwords found (offline — online breach check unavailable)',
  healthScore: 'Security score',
  verdictGood: 'good',
  verdictFair: 'fair',
  verdictPoor: 'poor',
  no2fa: 'No 2FA',
  genPw: 'Generate',
  genPwHint: 'Fill with a generated strong password',
  retry: 'Retry',
  genUser: 'Generate',
  genUserHint: 'Fill with a generated username',
  loadMore: 'Load more',
  clearsIn30s: 'clears in 30s',
  openUrl: 'Open',
  openUrlHint: 'Open the associated website',
  genOptsHint: 'Password generator options',
  genLength: 'Length',
  genOptUppercase: 'Uppercase',
  genOptLowercase: 'Lowercase',
  genOptDigits: 'Digits',
  genOptSymbols: 'Symbols',
  genOptExcludeAmbiguous: 'No ambiguous chars',
  strengthLabel: 'Strength',
  entropyLabel: 'Entropy',
  tplApply: 'Apply template',
  tplSave: 'Save as template',
  tplNamePrompt: 'Template name:',
  tplSaved: 'Template saved:',
  lock: 'Lock',
  recoveryCode: 'Generate recovery code',
  recoveryCodeConfirm: 'Generate a one-time recovery code? It is shown only once (store it safely, e.g. printed); only its hash is kept.',
  recoveryCodeAlert: 'Your one-time recovery code (shown once — store it safely):',
  recoveryCodeSet: 'Recovery code generated (shown once).',
  recoveryVerify: 'Verify recovery code',
  recoveryCodeVerifyPrompt: 'Enter the recovery code to verify:',
  recoveryVerified: 'Recovery code verified.',
  recoveryNotVerified: 'Recovery code does not match.',
  lockedMsg: 'Vault locked — unlock with vault_unlock',
  ageToday: 'new',
  ageHint: 'Updated days ago (password age)',
  cardExpired: 'Card expired',
  cardExpiring: 'Card expires',
  cardExpiryHint: 'Card expiry reminder',
  watchPoor: '⚠ high risk',
  watchWarn: '⚠ attention',
  watchScore: '{n}',
  watchFlagsTitle: 'Risks: ',
  resultCount: 'Results',
  totpUri: 'URI',
  totpUriHint: 'Show otpauth URI for another device',
  pwChangeConfirm: 'Changing the password — continue?',
  copyFieldHint: 'Copy this field value',
  tagManage: 'Tags',
  tagRename: 'Rename',
  tagRenamePrompt: 'New name for tag',
  genHistory: 'Recently generated',
  recentBackups: 'Recent backups',
  backupTitle: 'Encrypted backups',
  backupHint: 'Backups are encrypted copies of the vault document kept in the same folder. Restoring replaces the live vault with the selected snapshot; a safety copy of the current state is written first.',
  backupRestore: 'Restore',
  backupRestoreHint: 'Restore the vault from this backup',
  backupRestoreConfirm: 'Restore the vault from this backup? The current vault is snapshotted first, then replaced.',
  backupRestoreModeMerge: 'Merge this backup into the current vault (entries are copied in, existing same-title entries are kept)? OK = merge. Cancel = replace the whole vault.',
  backupRestoreModeReplace: 'Replace the WHOLE vault with this backup? A safety snapshot is written first.',
  backupDelete: 'Delete',
  backupDeleteConfirm: 'Delete this backup file permanently?',
  backupDeleted: 'Backup deleted.',
  backupSafetyHint: 'Pre-restore snapshot saved to',
  noBackups: 'No backups yet — create one with "Backup now".',
  permTitle: 'Access permissions & capture',
  systemImport: 'System import',
  importChrome: 'Import Chrome',
  importChromeDesc: 'Chrome password manager (Login Data)',
  importKeychain: 'Import Keychain',
  importKeychainDesc: 'macOS Keychain (internet passwords — website logins)',
  importKeychainHint: 'Reads internet passwords (inet) by default — the entries that actually back website logins (Wi-Fi and app secrets are generic passwords and are skipped). First-time authorization: choose "Always Allow" once for this process.',
  keychainPreview: 'Preview',
  preview: 'Preview',
  previewLabel: 'Preview —',
  keychainPromptWarn: 'Importing from the Keychain may prompt macOS for authorization for each entry. First time: choose "Always Allow" to consent once for this process. Continue?',
  systemSearch: 'Search system stores',
  sysSearchPlaceholder: 'Search Chrome / Keychain…',
  sysSearchGo: 'Search',
  importFirefox: 'Import Firefox',
  importFirefoxDesc: 'Firefox profile (logins.json + key4.db)',
  firefoxMasterPw: 'Firefox primary password (leave empty if none):',
  import1password: 'Import 1Password',
  import1passwordDesc: '1Password 1PUX export (.1pux)',
  import1passwordPrompt: 'Path to the .1pux file:',
  importManagerCsv: 'Import manager CSV',
  importManagerCsvDesc: 'Password-manager CSV (Dashlane / NordPass / Keeper)',
  importManagerCsvPrompt: 'Path to the CSV file (header auto-detected):',
  importEnpass: 'Import Enpass',
  importEnpassDesc: 'Enpass JSON export (.json)',
  importEnpassPrompt: 'Path to the Enpass .json export:',
  importBitwarden: 'Import Bitwarden',
  importBitwardenDesc: 'Bitwarden JSON export (.json)',
  importBitwardenPrompt: 'Path to the Bitwarden .json export:',
  importBitwardenEncrypted: 'Import Bitwarden (encrypted)',
  importBitwardenEncryptedDesc: 'Bitwarden password-protected JSON export (.json)',
  importBitwardenEncryptedPrompt: 'Path to the encrypted Bitwarden .json export:',
  importBitwardenEncryptedPasswordPrompt: 'Export passphrase (the one chosen when creating the encrypted export):',
  import1pif: 'Import 1Password 1PIF',
  import1pifDesc: 'Legacy 1Password 1PIF export (.1pif)',
  import1pifPrompt: 'Path to the .1pif file:',
  importKeePassXml: 'Import KeePass XML',
  importKeePassXmlDesc: 'KeePass 2.x XML export (.xml)',
  importKeePassXmlPrompt: 'Path to the KeePass .xml export:',
  importKdbx: 'Import KeePass KDBX',
  importKdbxDesc: 'KeePass KDBX database (.kdbx, 3.1/4.x, AES-KDF/Argon2)',
  importKdbxPrompt: 'Path to the .kdbx file:',
  importKdbxPasswordPrompt: 'Database password (leave blank if none):',
  exportTitle: 'Export',
  exportHint: 'Export the whole vault (contains plaintext secrets — keep the file protected).',
  export1pux: 'Export 1Password 1PUX',
  export1puxDesc: '1Password 1PUX archive (.1pux)',
  export1puxPrompt: 'Path to the output .1pux file:',
  exportDefaultPath: 'dsh-vault-export-{file}',
  exportPathHint: 'Suggested path (edit if needed)',
  exportBitwarden: 'Export Bitwarden JSON',
  exportBitwardenDesc: 'Bitwarden JSON document (.json)',
  exportBitwardenPrompt: 'Path to the output .json file:',
  exportCsv: 'Export entries CSV',
  exportCsvDesc: 'Entries as CSV (pick the columns below)',
  exportCsvPrompt: 'Path to the output .csv file:',
  exportNoFields: 'Select at least one column to export.',
  exportColumns: 'columns',
  iconPickerHint: 'Common icons',
  colorPickerHint: 'Preset colors',
  badgeEditHint: 'Open this entry for editing',
  recentSearches: 'Recent',
  recentSearchHint: 'Run this search again',
  clearRecent: 'Clear',
  tagManager: 'Tags',
  tagManagerHint: 'Rename or remove tags across all entries',
  noTags: 'No tags yet.',
  tagRemove: 'Remove',
  tagRemoveConfirm: 'Remove the tag "{name}" from every entry?',
  tagRemoved: 'Removed the tag from {n} entries.',
  exportDone: 'Exported',
  sectionTabs: 'Vault sections',
  tabEntries: 'Entries',
  tabSecurity: 'Security',
  tabTransfer: 'Import / Export',
  tabBackup: 'Backup',
  tabPermissions: 'Permissions',
  tabSessions: 'Sessions',
  tabAudit: 'Audit',
  recentActivityEmpty: 'No activity yet.',
  auditFilter: 'Filter by action',
  auditAll: 'All actions',
  auditAdd: 'Add',
  auditUpdate: 'Update',
  auditDelete: 'Delete',
  auditRestore: 'Restore',
  auditPurge: 'Purge',
  auditMerge: 'Merge',
  auditRollback: 'Rollback',
  auditRead: 'Read',
  auditSearch: 'Search',
  auditTotp: 'TOTP code',
  auditSwitch: 'Vault switch',
  auditSecretNote: 'Audit stores action, entry title and time only — never passwords, keys or other secrets.',
  auditWriteQuick: 'Changes',
  bulkExportCsv: 'Export CSV',
  bulkExportHint: 'Export the selected entries to a CSV file',
  bulkExportPath: 'CSV output path (selected entries):',
  auditQuick: 'Quick filter',
  loadMoreAudit: 'Load more ({n})',
  auditCount: '{n} events',
  auditFiltered: 'filtered',

  auditJumpHint: 'Jump to this entry',
  auditExport: 'Export log',
  tabTrash: 'Trash',
  sessionsIntro: 'Log into any site in a real browser window, then save the session cookies here for automation (curl, requests, browser-less tools). HttpOnly cookies are captured too.',
  sessionUrlPrompt: 'Site URL to open:',
  sessionOpen: 'Open login window',
  sessionOpenHint: 'Opens a real browser window at the URL. Log in there manually (password, 2FA, …), then come back and click Collect.',
  sessionOpenFailed: 'Could not open a browser — is playwright-core installed and a Chromium build available?',
  sessionOpenList: 'Open windows',
  sessionNoOpen: 'No browser windows open.',
  sessionCollect: 'Collect & save',
  sessionSaveTitle: 'Save as:',
  sessionOverwrite: 'Replace existing',
  sessionSaved: 'saved {n} cookies',
  sessionSavedList: 'Saved sessions',
  sessionEmpty: 'No saved login sessions yet. Open a window, log in, and collect.',
  sessionExport: 'Copy Cookie header',
  sessionExportJar: 'Copy jar (curl -b)',
  sessionExportPlaywright: 'Copy Playwright snippet',
  sessionView: 'View',
  sessionDelete: 'Delete',
  sessionClose: 'Close window',
  sessionCopied: 'Copied!',
  sessionDetailTitle: 'Cookies',
  sessionCookie: 'cookies',
  sessionExpires: 'expires',
  sessionSessionCookie: 'session',
  sessionExpired: 'expired',
  sessionExpiringSoon: 'expiring in 7d',
  sessionPrune: 'Prune expired',
  sessionPruneConfirm: 'Remove expired cookies from this saved session? Session cookies (no expiry) are kept.',
  sessionDomain: 'domain',
  sessionReopen: 'Re-open in browser',
  sessionImportPaste: 'Import from pasted cookies',
  sessionImportPastePrompt: 'Paste a JSON cookie array or a Cookie header string:',
  sessionImportPasteTitle: 'Title:',
  sessionImport: 'Import cookies',
  sessionNoteOpen: 'Browser window opened at {url}. Log in, then click Collect & save.',
  sessionNoteCollect: 'Collected {n} cookies.',
  sessionNamePlaceholder: 'e.g. GitHub session',
  sessionUrlPlaceholder: 'https://example.com/login',
  sessionReadOnly: 'The vault is in readonly mode — browser sessions cannot be saved.',
  lockedShort: '🔒 locked',
  unlock: 'Unlock',
  httpSites: 'HTTP sites',
  healthStrength: 'strength',
  dueExpired: 'EXPIRED',
  dueExpiring: 'expires in',
  dueNow: 'rotation due',
  showTrash: 'Trash',
  hideTrash: 'Hide trash',
  trashed: 'in trash',
  trashEmpty: 'Trash is empty',
  trashEmptyHint: 'Deleted credentials wait here. Nothing is purged automatically: an entry stays recoverable until you restore it or empty the trash.',
  // Relative ages, shown on hover next to an absolute timestamp.
  relJustNow: 'just now',
  relMinutes: '{n} min ago',
  relHours: '{n} h ago',
  relDays: '{n} d ago',
  restore: 'Restore',
  clearTrash: 'Empty trash',
  restoreAll: 'Restore all',
  clearTrashConfirm: 'Permanently delete ALL trashed entries? This cannot be undone.',
  show: 'Show',
  hide: 'Hide',
  allKinds: 'All kinds',
  allTags: 'All tags',
  sortBy: 'Sort',
  sortAlpha: 'Sort: A–Z',
  sortFavorite: 'Favorites first',
  sortSmart: 'Smart',
  sortRecent: 'Sort: recent',
  sortCreated: 'Sort: created',
  highSensitivity: 'HIGH',
  pinned: 'Pinned',
  togglePinHint: 'Toggle pinned (shows at top in pinned/smart sort)',
  attachmentsTitle: 'Attachments',
  detachHint: 'Remove attachment',
  add: 'Add credential',
  quickAdd: 'Quick add',
  cancel: 'Cancel',
  save: 'Save',
  saveShortcut: 'Ctrl/⌘ + Enter',
  addShortcut: 'Ctrl/⌘ + N — new entry',
  searchShortcut: 'Ctrl/⌘ + K — focus search; Esc — clear',
  savedNew: 'Entry created.',
  savedUpdated: 'Entry updated.',
  edit: 'Edit',
  delete: 'Delete',
  deleteConfirm: 'Move this entry to the trash? You can restore it later.',
  deleted: 'Entry moved to trash.',
  deletedWithTitle: '{name} moved to trash.',
  copy: 'Copy',
  copyUsername: 'Copy username',
  copyUsernameHint: 'Copy the username',
  copyTitleHint: 'Copy the title',
  pwHistory: 'Password history',
  pwHistoryHint: 'View past passwords for this entry',
  pwHistoryEmpty: 'No password history yet.',
  pwRollback: 'Restore',
  pwRollbackConfirm: 'Restore this old password? The current one moves to history.',
  pwRolledBack: 'Password restored from history.',
  pwRollbackFailed: 'Could not restore that password.',
  bulkSelect: 'Select',
  bulkDone: 'Done',
  bulkSelectAll: 'Select all',
  bulkSelected: 'Selected',
  bulkDelete: 'Delete selected',
  bulkDeleteConfirm: 'Delete {n} selected entries? They move to the trash.',
  bulkDeleted: 'Deleted {n} entries.',
  copyPassword: 'Copy password',
  copyCode: 'Copy code',
  copyApiKey: 'Copy API key',
  copyToken: 'Copy token',
  copyCardNumber: 'Copy card number',
  copyKey: 'Copy secret',
  nothingToCopy: 'This entry has no password or key to copy.',
  copied: 'Copied',
  bulkFavorite: 'Favorite selected',
  bulkUnfavorite: 'Unfavorite selected',
  bulkTag: 'Add tag',
  bulkTagPrompt: 'Tag to add to the selected entries:',
  bulkFavorited: 'Favorited {n} entries.',
  bulkUnfavorited: 'Unfavorited {n} entries.',
  bulkTagged: 'Tagged {n} entries.',
  totp: 'Code',
  totpInlineHint: 'Current one-time password — auto-refreshes every 30 seconds. Click ⧉ to copy.',
  totpRowCopyHint: 'Click the code to copy it (auto-refreshes every 30s)',
  clone: 'Clone',
  cloneSuffix: ' (copy)',
  clonedWithTitle: 'Cloned as "{name}".',
  searchResultsCount: 'Search: {n} matches',
  previewImportTitle: 'CSV import preview',
  previewImportRows: '{n} rows, {m} skipped',
  confirmImport: 'Import {n} entries',
  attachFile: 'Add attachment',
  attachHint: 'Pick a file to attach (encrypted at rest)',
  downloadHint: 'Download attachment',
  attachmentNotFound: 'Attachment not found.',
  favOnly: 'Favorites',
  favOnlyHint: 'Show only pinned (favorite) entries',
  dueOnly: 'Due',
  dueOnlyHint: 'Show only expired / due / expiring-soon entries',
  issueOnly: 'Issues',
  issueOnlyHint: 'Show only entries with health issues (weak / reused / no 2FA / HTTP)',
  copyAllFields: 'Copy all fields',
  copyAllHint: 'Copy this entry as plain text (one field per line)',
  density: 'Density',
  densityHint: 'Toggle list density (compact / comfortable)',
  attachmentCountHint: 'This entry has attached files (count shown)',
  bulkCopyTitles: 'Copy titles',
  vaultManageTitle: 'Vault management',
  vaultManageDesc: 'Current vault',
  vaultNew: 'New vault',
  vaultNewPrompt: 'New vault name (empty vault, same master password):',
  vaultNameInvalid: 'Invalid name — avoid reserved words like access/meta/-audit.',
  favorite: '★ Add to favorites',
  unfavorite: '☆ Remove from favorites',
  favoriteAria: 'Add to favorites',
  unfavoriteAria: 'Remove from favorites',
  favEmptyHint: 'No favorites yet — click the ☆ next to an entry title to pin it.',
  fieldTitle: 'Title',
  fieldKind: 'Kind',
  fieldUsername: 'Username',
  fieldEmail: 'Email',
  fieldPhone: 'Phone',
  fieldPassword: 'Password',
  fieldHost: 'Host',
  fieldPort: 'Port',
  fieldApiKey: 'API key',
  fieldSecret: 'Secret',
  fieldAccessToken: 'Access token',
  fieldRefreshToken: 'Refresh token',
  fieldOtpSecret: 'OTP secret',
  fieldUrl: 'URL',
  fieldCardNumber: 'Card number',
  fieldCardExpiry: 'Expiry (MM/YY)',
  fieldCardCvv: 'CVV',
  fieldCardHolder: 'Card holder',
  fieldNotes: 'Notes',
  fieldTags: 'Tags',
  fieldIcon: 'Icon (emoji)',
  fieldExpiresAt: 'Expires at',
  fieldEnvKey: 'Env var names (optional)',
  fieldEnvKeyHint: 'Exact environment-variable names for vault_env, comma-separated and positional: the first names the main secret, the second the next one (e.g. DASHSCOPE_API_KEY, or GOOGLE_ACCESS_TOKEN, GOOGLE_REFRESH_TOKEN). Leave empty to derive them from the title.',
  fieldRotationDays: 'Rotation (days, 0 = never)',
  fieldSensitivity: 'Sensitivity',
  fieldFavorite: 'Pinned',
  sensitivityNormal: 'normal',
  sensitivityHigh: 'high',
  favoriteHint: 'Show at top',
  rotationClearHint: '0 clears rotation',
  fieldColor: 'Color',
  fieldCustom: 'Custom fields',
  fieldCreatedAt: 'Created',
  fieldUpdatedAt: 'Updated',
  genLenChips: 'Quick lengths',
  riskWeakText: '⚠️ This password is weak (aim for 12+ mixed characters — or generate one).',
  riskReusedText: '⚠️ This password is already used by {n} other entries.',
  urlLogoText: 'will show this site logo',
  urlLogoHint: 'This entry will display the site logo in the list (unless you pick a custom icon).',
  customKeyPh: 'key',
  customValuePh: 'value',
  addField: '+ Add field',
  customRemove: 'Remove this field',
  error: 'Something went wrong.',
  errLocked: 'Vault is locked — unlock first.',
  errTitleEmpty: 'Title must not be empty.',
  errPathEmpty: 'Path must not be empty.',
  entryNotFound: 'Entry not found.',
  errInvalidPort: 'Port must be a number (1-65535).',
  errInvalidVaultName: 'Invalid vault name.',
  errReadonly: 'This action is disabled in readonly mode.',
  errDetail: 'Something went wrong: {detail}',
  autoLockLabel: 'Auto-lock after',
  autoLockNever: 'Never',
  autoLock1m: '1 minute',
  autoLock5m: '5 minutes',
  autoLock15m: '15 minutes',
  autoLock30m: '30 minutes',
  autoLock1h: '1 hour',
  autoLockHint: 'Auto-lock',
  autoLockNeverHint: 'auto-lock off',
  purge: 'Delete permanently',
  purgeOneConfirm: 'Permanently delete "{name}"? This cannot be undone.',
  genPassphrase: 'Memorable passphrase',
  genWords: 'Words (2–12)',
  genSeparator: 'Separator',
  genWordDigits: 'Append 2 digits',
  kindLogin: 'Login',
  kindSsh: 'SSH',
  kindApiKey: 'API key',
  kindSecret: 'Secret',
  kindOauth: 'OAuth',
  kindCookie: 'Session cookies',
  kindCard: 'Card',
  kindCustom: 'Custom',
} as const

export type VaultLocaleKey = keyof typeof en

/** Chinese strings (same keys as English). */
export const zh: Record<VaultLocaleKey, string> = {
  nav: '凭据库',
  heading: 'Vault 凭据库',
  intro: '本机加密存储的凭据。',
  searchPlaceholder: '搜索标题、用户名、邮箱、主机…',
  loading: '加载中…',
  empty: '还没有凭据，添加一条开始使用。',
  noFiltered: '当前过滤条件下没有匹配的条目。',
  noFilteredHint: '试试其他搜索词, 或清除过滤条件。',
  clearFilters: '清除过滤',
  emptyHint: '提示：你可以在对话中把 API Key 或 token 交给助手保存——开启自动捕获后，它会在你同意时帮你存入本库。批量导入？让助手调用 vault_import_csv 并给出 CSV 路径即可。',
  emptyHintReadonly: '当前为只读模式——只能读取凭据，不能在此添加。将 accessMode 改为 "readwrite" 即可启用写入。',
  modeLabel: '访问模式',
  autoCaptureLabel: '自动捕获对话中的凭据',
  modeReadonly: '只读',
  modeAsk: '写入前询问',
  modeAuto: '自动读写',
  modeReadonlyHint: '只读 —— 变更已禁用',
  modeAskHint: '写入前询问 —— 每次增/改/删都会征求你的确认',
  modeAutoHint: '自动读写 —— 无需逐次确认',
  autoCaptureOn: '自动捕获已开启',
  autoCaptureOff: '自动捕获已关闭',
  clearSearch: '清除搜索',
  entryCount: '条目数',
  reportTitle: '健康与轮换',
  reportJumpHint: '打开该条目',
  recentActivity: '最近活动',
  recentlyAdded: '最近添加',
  reportRotation: '已过期 / 待轮换',
  reportWeak: '弱密码',
  healthSummaryHint: '保险库健康: 弱密码/复用/缺2FA/HTTP站点/到期轮换计数',
  reportReused: '跨条目复用的凭据',
  dupGroups: '重复分组',
  dupTitle: '重复条目',
  dupMerge: '合并',
  badgeFilterHint: '点击将列表筛选到该问题；再点一次取消',
  kindChipHint: '点击按该类型筛选；再点一次取消',
  dueFilterHint: '点击仅显示已过期 / 待轮换条目',
  healthOk: '凭据库健康',
  sessionCollectHint: '收集该浏览器窗口的 cookies 并保存为会话',
  vaultSwitchHint: '切换保险库(对所有标签页生效)',
  vaultTargetLabel: '保险库',
  vaultTargetHint: '选择下面按钮要操作的保险库',
  vaultRenameHint: '重命名所选保险库',
  vaultDeleteHint: '删除所选保险库(default 库不可删除)',
  vaultNewHint: '用同一主密码新建一个空保险库',
  toolProfileCustom: '自定义…',
  toolGroupManagement: '管理',
  toolGroupIo: '导入导出',
  toolGroupSessions: '浏览器会话',
  toolGroupFiles: '备份与文件',
  toolProfile: '模型工具集',
  toolProfileHint: '基础档让模型的工具列表保持精简;任务需要更多能力时切到标准/完整(或自定义)。切换立即生效。',
  toolProfileHintLong: '模型可调用的保险库工具范围。基础档 = 增删改查、搜索、生成、TOTP;标准档增加保险库管理(收藏、标签、到期、健康、重复、回收站、附件、备份);完整档再加批量导入导出、浏览器会话与保险库文件操作;自定义 = 基础 + 自行勾选的分组。切换立即生效,并按保险库目录持久化。',
  toolProfileBasic: '基础(推荐)',
  toolProfileStandard: '标准',
  toolProfileFull: '完整',
  toolProfileApplied: '已应用工具集 —— 注册 {n} 个工具',
  previewShort: '预览',
  copySecretHint: '复制该条目的密钥/密码/令牌 —— 剪贴板 30 秒后自动清空',
  sysSearchHint: '在 Chrome 与 macOS 钥匙串中搜索该关键词(只显示名称, 不含密码)',
  exportCsvHint: '把全部条目的所选字段导出为 CSV 文件(明文!)',
  tagRenameHint: '在所有条目中重命名此标签',
  tagRemoveHint: '从所有条目中移除此标签',
  sessionImportHint: '粘贴导出的 cookies(JSON 数组或 Cookie 头), 保存为会话条目',
  auditQuickHint: '快捷过滤: 全部 / 读取 / 搜索 / 变更',
  auditExportHint: '把当前审计视图导出为 CSV',
  auditLoadMoreHint: '显示更多审计事件',
  importNameChrome: 'Chrome',
  importNameFirefox: 'Firefox',
  importNameKeychain: 'macOS 钥匙串',
  importName1password: '1Password',
  importNameBitwarden: 'Bitwarden',
  importNameBitwardenEncrypted: 'Bitwarden(加密)',
  importName1pif: '1Password 1PIF',
  importNameKeePassXml: 'KeePass XML',
  importNameKdbx: 'KeePass KDBX',
  importNameEnpass: 'Enpass',
  importNameManagerCsv: '密码库 CSV',
  importIntro: '选择来源: 先预览(预览不会写入), 确认后再导入。',
  importRun: '导入',
  importChromeShort: '本机 Chrome 中保存的密码',
  importFirefoxShort: '本机 Firefox profile 中的密码',
  importKeychainShort: 'macOS 钥匙串(网页登录与通用密码)',
  import1passwordShort: '1Password 导出文件(.1pux)',
  importBitwardenShort: 'Bitwarden / Vaultwarden JSON 导出',
  importBitwardenEncryptedShort: '带密码保护的 Bitwarden JSON',
  import1pifShort: '旧版 1Password 导出(.1pif)',
  importKeePassXmlShort: 'KeePass 2.x XML 导出',
  importKdbxShort: 'KeePass 数据库文件(.kdbx 3.1/4.x)',
  importEnpassShort: 'Enpass JSON 导出',
  importManagerCsvShort: '任意密码管理器 CSV(自动识别列)',
  grpChecks: '安全检查',
  grpBackup: '备份',
  grpAccess: '访问与恢复',
  exportReportHint: '把本健康/安全报告(弱密码/复用/2FA/HTTP/轮换)导出为 CSV',
  breachHint: '对照 Have I Been Pwned 检查已存密码是否泄露(联网; 只发送 SHA-1 哈希前缀)',
  backupNowHint: '立即创建加密备份副本(保留最近 N 份, 自动清理更早)',
  lockHint: '清空内存密钥并锁定保险库(解锁需要主密码)',
  recoveryGenHint: '生成一次性恢复码: 主密码遗失时用于验证所有权',
  recoveryVerHint: '验证你持有之前生成的恢复码',
  statBadgeHint: '保险库统计概览。点击健康徽章(弱密码/复用/2FA/HTTP/到期)可直接筛选列表。',
  healthBackup: '备份',
  backupNow: '立即备份',
  backupDone: '备份已创建',
  lockedBanner: '保险库已锁定 —— 请使用 vault_unlock 解锁后再读写条目。',
  vaultSelect: '当前保险库',
  vaultRename: '重命名保险库',
  vaultRenamePrompt: '保险库的新名称',
  vaultDelete: '删除',
  vaultDeleteConfirm: '永久删除保险库 "{name}"?此操作无法撤销。',
  vaultDeleteDefault: '默认保险库不能被删除。',
  touch: '标记已用',
  touchHint: '标记为最近使用',
  auditTitle: '需要处理',
  auditEdit: '编辑',
  breachCheck: '泄露检测',
  exportReport: '导出安全报告',
  reportExported: '安全报告已导出为 CSV。',
  breachTitle: '泄露扫描',
  breachPwned: '密码曾出现在数据泄露中',
  breachWeak: '常见/弱密码',
  breachOffline: '离线兜底',
  breachOk: '未发现泄露或弱密码',
  breachOkOffline: '未发现弱密码(离线——在线泄露检测不可用)',
  healthScore: '安全评分',
  verdictGood: '良好',
  verdictFair: '一般',
  verdictPoor: '较差',
  no2fa: '未启用2FA',
  genPw: '生成',
  genPwHint: '填入生成的强密码',
  retry: '重试',
  genUser: '生成',
  genUserHint: '填入生成的用户名',
  loadMore: '加载更多',
  clearsIn30s: '30秒后自动清除',
  openUrl: '打开',
  openUrlHint: '打开关联网站',
  genOptsHint: '密码生成器选项',
  genLength: '长度',
  genOptUppercase: '大写',
  genOptLowercase: '小写',
  genOptDigits: '数字',
  genOptSymbols: '符号',
  genOptExcludeAmbiguous: '排除易混淆字符',
  strengthLabel: '强度',
  entropyLabel: '熵值',
  tplApply: '套用模板',
  tplSave: '存为模板',
  tplNamePrompt: '模板名称：',
  tplSaved: '模板已保存：',
  lock: '锁定',
  recoveryCode: '生成恢复码',
  recoveryCodeConfirm: '生成一次性恢复码？仅在生成时显示一次（请妥善保存,如打印备份）;只保存其哈希。',
  recoveryCodeAlert: '您的一次性恢复码（仅显示一次——请妥善保存）:',
  recoveryCodeSet: '恢复码已生成（仅显示一次）。',
  recoveryVerify: '验证恢复码',
  recoveryCodeVerifyPrompt: '输入要验证的恢复码:',
  recoveryVerified: '恢复码验证通过。',
  recoveryNotVerified: '恢复码不匹配。',
  lockedMsg: '保险库已锁定 —— 使用 vault_unlock 解锁',
  ageToday: '新',
  ageHint: '距上次更新天数(密码年龄)',
  cardExpired: '卡已过期',
  cardExpiring: '卡将到期',
  cardExpiryHint: '卡到期提醒',
  watchPoor: '⚠ 高风险',
  watchWarn: '⚠ 需关注',
  watchScore: '{n}',
  watchFlagsTitle: '风险: ',
  resultCount: '结果',
  totpUri: 'URI',
  totpUriHint: '显示 otpauth URI 以便在其它设备添加',
  pwChangeConfirm: '正在修改密码 —— 确定继续？',
  copyFieldHint: '复制该字段值',
  tagManage: '标签管理',
  tagRename: '重命名',
  tagRenamePrompt: '标签新名称',
  genHistory: '最近生成',
  recentBackups: '最近备份',
  backupTitle: '加密备份',
  backupHint: '备份是存放在同目录下的加密副本。还原会用所选快照替换当前密码库，还原前会先保存一份当前状态的安全快照。',
  backupRestore: '还原',
  backupRestoreHint: '用该备份还原密码库',
  backupRestoreConfirm: '确定用该备份还原密码库？当前状态会先快照，再被替换。',
  backupRestoreModeMerge: '将该备份合并进当前密码库（条目被复制进来,同标题条目保留）？确定=合并。取消=整体替换密码库。',
  backupRestoreModeReplace: '用该备份整体替换当前密码库？会先写一份安全快照。',
  backupDelete: '删除',
  backupDeleteConfirm: '永久删除这个备份文件？',
  backupDeleted: '备份已删除。',
  backupSafetyHint: '还原前快照已保存到',
  noBackups: '暂无备份 — 用“立即备份”创建。',
  permTitle: '访问权限与自动捕获',
  systemImport: '系统导入',
  importChrome: '导入 Chrome',
  importChromeDesc: 'Chrome 密码管理器（Login Data）',
  importKeychain: '导入钥匙串',
  importKeychainDesc: 'macOS 钥匙串（互联网密码——网站登录凭据）',
  importKeychainHint: '默认读取互联网密码（inet）——真正用于网站登录的条目（Wi-Fi 和应用密钥属于通用密码,会被跳过）。首次授权请选择「始终允许」一次即可。',
  keychainPreview: '预览',
  preview: '预览',
  previewLabel: '预览 —',
  keychainPromptWarn: '从钥匙串导入时 macOS 可能为每条凭据弹出授权框。首次请在弹窗中选择“始终允许”一次性授权本进程。是否继续？',
  systemSearch: '搜索系统凭据库',
  sysSearchPlaceholder: '搜索 Chrome / 钥匙串…',
  sysSearchGo: '搜索',
  importFirefox: '导入 Firefox',
  importFirefoxDesc: 'Firefox 配置（logins.json + key4.db）',
  firefoxMasterPw: 'Firefox 主密码（未设置则留空）：',
  import1password: '导入 1Password',
  import1passwordDesc: '1Password 1PUX 导出（.1pux）',
  import1passwordPrompt: '请输入 .1pux 文件路径：',
  importManagerCsv: '导入密码库 CSV',
  importManagerCsvDesc: '密码管理器 CSV（Dashlane / NordPass / Keeper）',
  importManagerCsvPrompt: '请输入 CSV 文件路径（自动识别表头）：',
  importEnpass: '导入 Enpass',
  importEnpassDesc: 'Enpass JSON 导出（.json）',
  importEnpassPrompt: '请输入 Enpass JSON 导出文件路径：',
  importBitwarden: '导入 Bitwarden',
  importBitwardenDesc: 'Bitwarden JSON 导出（.json）',
  importBitwardenPrompt: '请输入 Bitwarden JSON 导出文件路径：',
  importBitwardenEncrypted: '导入 Bitwarden（加密）',
  importBitwardenEncryptedDesc: 'Bitwarden 加密 JSON 导出（.json）',
  importBitwardenEncryptedPrompt: '请输入加密 Bitwarden JSON 导出文件路径：',
  importBitwardenEncryptedPasswordPrompt: '导出口令（创建加密导出时设置的口令）：',
  import1pif: '导入 1Password 1PIF',
  import1pifDesc: '旧版 1Password 1PIF 导出（.1pif）',
  import1pifPrompt: '请输入 .1pif 文件路径：',
  importKeePassXml: '导入 KeePass XML',
  importKeePassXmlDesc: 'KeePass 2.x XML 导出（.xml）',
  importKeePassXmlPrompt: '请输入 KeePass XML 导出文件路径：',
  importKdbx: '导入 KeePass KDBX',
  importKdbxDesc: 'KeePass KDBX 数据库（.kdbx,3.1/4.x,AES-KDF/Argon2）',
  importKdbxPrompt: '请输入 .kdbx 文件路径：',
  importKdbxPasswordPrompt: '数据库密码（无密码则留空）：',
  exportTitle: '导出',
  exportHint: '导出整个保险库（包含明文机密——请妥善保护文件）。',
  export1pux: '导出 1Password 1PUX',
  export1puxDesc: '1Password 1PUX 归档（.1pux）',
  export1puxPrompt: '输出 .1pux 文件路径：',
  exportDefaultPath: 'dsh-vault-export-{file}',
  exportPathHint: '建议路径（可按需修改）',
  exportBitwarden: '导出 Bitwarden JSON',
  exportBitwardenDesc: 'Bitwarden JSON 文档（.json）',
  exportBitwardenPrompt: '输出 .json 文件路径：',
  exportCsv: '导出条目 CSV',
  exportCsvDesc: '条目导出为 CSV（勾选下方导出字段）',
  exportCsvPrompt: '输出 .csv 文件路径：',
  exportNoFields: '请至少勾选一个导出字段。',
  exportColumns: '列',
  iconPickerHint: '常用图标',
  colorPickerHint: '预设颜色',
  badgeEditHint: '点击编辑此条目',
  recentSearches: '最近搜索',
  recentSearchHint: '再次执行此搜索',
  clearRecent: '清除',
  tagManager: '标签',
  tagManagerHint: '跨全部条目重命名或移除标签',
  noTags: '暂无标签。',
  tagRemove: '移除',
  tagRemoveConfirm: '从所有条目移除标签「{name}」？',
  tagRemoved: '已从 {n} 条条目移除该标签。',
  exportDone: '已导出',
  sectionTabs: '凭据库分区',
  tabEntries: '条目',
  tabSecurity: '安全',
  tabTransfer: '导入/导出',
  tabBackup: '备份',
  tabPermissions: '权限',
  tabSessions: '会话',
  tabAudit: '审计',
  recentActivityEmpty: '暂无活动记录。',
  auditFilter: '按操作类型筛选',
  auditAll: '全部操作',
  auditAdd: '新增',
  auditUpdate: '更新',
  auditDelete: '删除',
  auditRestore: '恢复',
  auditPurge: '彻底删除',
  auditMerge: '合并',
  auditRollback: '回滚',
  auditRead: '读取',
  auditSearch: '搜索',
  auditTotp: '生成验证码',
  auditSwitch: '切换保险库',
  auditSecretNote: '审计仅记录操作类型、条目标题与时间 —— 绝不包含密码、密钥等任何敏感值。',
  auditWriteQuick: '变更',
  bulkExportCsv: '导出所选 CSV',
  bulkExportHint: '把选中的条目导出为 CSV 文件',
  bulkExportPath: 'CSV 输出路径(所选条目):',
  auditQuick: '快捷过滤',
  loadMoreAudit: '加载更多 ({n})',
  auditCount: '共 {n} 条',
  auditFiltered: '已过滤',

  auditJumpHint: '跳转到该条目',
  auditExport: '导出日志',
  tabTrash: '回收站',
  sessionsIntro: '在真实浏览器窗口中登录任意网站,然后把登录后的 session cookie 保存到这里,供自动化任务使用(curl、requests 等)。HttpOnly cookie 也能捕获。',
  sessionUrlPrompt: '要打开的网站地址:',
  sessionOpen: '打开登录窗口',
  sessionOpenHint: '在指定地址打开真实浏览器窗口。请在其中手动登录(密码、二次验证等),然后回到这里点击「收集并保存」。',
  sessionOpenFailed: '无法打开浏览器——请确认已安装 playwright-core 且有可用的 Chromium。',
  sessionOpenList: '已打开的窗口',
  sessionNoOpen: '当前没有打开的浏览器窗口。',
  sessionCollect: '收集并保存',
  sessionSaveTitle: '保存为:',
  sessionOverwrite: '覆盖已有条目',
  sessionSaved: '已保存 {n} 个 cookie',
  sessionSavedList: '已保存的会话',
  sessionEmpty: '还没有保存的登录会话。打开窗口、登录、然后收集。',
  sessionExport: '复制 Cookie 头',
  sessionExportJar: '复制 cookie jar(curl -b)',
  sessionExportPlaywright: '复制 Playwright 片段',
  sessionView: '查看',
  sessionDelete: '删除',
  sessionClose: '关闭窗口',
  sessionCopied: '已复制!',
  sessionDetailTitle: 'Cookies',
  sessionCookie: '个 cookie',
  sessionExpires: '过期',
  sessionSessionCookie: '会话',
  sessionExpired: '已过期',
  sessionExpiringSoon: '7 天内过期',
  sessionPrune: '清理过期',
  sessionPruneConfirm: '移除该已保存会话中的过期 cookie?无过期的会话 cookie 会保留。',
  sessionDomain: '域名',
  sessionReopen: '在浏览器中重新打开',
  sessionImportPaste: '从粘贴的 cookie 导入',
  sessionImportPastePrompt: '粘贴 JSON cookie 数组或 Cookie 头字符串:',
  sessionImportPasteTitle: '标题:',
  sessionImport: '导入 cookies',
  sessionNoteOpen: '浏览器窗口已打开:{url}。登录后点击「收集并保存」。',
  sessionNoteCollect: '已收集 {n} 个 cookie。',
  sessionNamePlaceholder: '例如:GitHub 会话',
  sessionUrlPlaceholder: 'https://example.com/login',
  sessionReadOnly: '凭据库处于只读模式,无法保存浏览器会话。',
  lockedShort: '🔒 已锁定',
  unlock: '解锁',
  httpSites: 'HTTP站点',
  healthStrength: '强度',
  dueExpired: '已过期',
  dueExpiring: '将于',
  dueNow: '待轮换',
  showTrash: '回收站',
  hideTrash: '隐藏回收站',
  trashed: '已删除',
  trashEmpty: '回收站为空',
  trashEmptyHint: '删除的凭据会先留在这里。本插件不会自动清理:条目一直可恢复,直到你手动恢复它、或清空回收站彻底删除。',
  // 相对时间,作为绝对时间戳的悬停提示。
  relJustNow: '刚刚',
  relMinutes: '{n} 分钟前',
  relHours: '{n} 小时前',
  relDays: '{n} 天前',
  restore: '恢复',
  clearTrash: '清空回收站',
  restoreAll: '全部恢复',
  clearTrashConfirm: '永久删除回收站中全部条目？此操作不可撤销。',
  show: '显示',
  hide: '隐藏',
  allKinds: '全部类型',
  allTags: '全部标签',
  sortBy: '排序',
  sortAlpha: '排序：A–Z',
  sortFavorite: '置顶优先',
  sortSmart: '智能排序',
  sortRecent: '排序：最近',
  sortCreated: '排序：创建时间',
  highSensitivity: '高敏',
  pinned: '已置顶',
  togglePinHint: '切换置顶（置顶/智能排序时显示在最前）',
  attachmentsTitle: '附件',
  detachHint: '移除附件',
  add: '新增凭据',
  quickAdd: '快速添加',
  cancel: '取消',
  save: '保存',
  saveShortcut: 'Ctrl/⌘ + Enter',
  addShortcut: 'Ctrl/⌘ + N — 新增凭据',
  searchShortcut: 'Ctrl/⌘ + K — 聚焦搜索; Esc — 清空',
  savedNew: '条目已创建。',
  savedUpdated: '条目已更新。',
  edit: '编辑',
  delete: '删除',
  deleteConfirm: '将该条目移入回收站？之后可恢复。',
  deleted: '条目已移入回收站。',
  deletedWithTitle: '「{name}」已移入回收站。',
  copy: '复制',
  copyUsername: '复制用户名',
  copyUsernameHint: '复制用户名',
  copyTitleHint: '复制标题',
  pwHistory: '密码历史',
  pwHistoryHint: '查看该条目的历史密码',
  pwHistoryEmpty: '暂无密码历史。',
  pwRollback: '恢复',
  pwRollbackConfirm: '恢复此旧密码？当前密码将移入历史。',
  pwRolledBack: '已从历史恢复密码。',
  pwRollbackFailed: '无法恢复该密码。',
  bulkSelect: '批量选择',
  bulkDone: '完成',
  bulkSelectAll: '全选',
  bulkSelected: '已选择',
  bulkDelete: '删除所选',
  bulkDeleteConfirm: '删除所选 {n} 条条目？它们将移入回收站。',
  bulkDeleted: '已删除 {n} 条条目。',
  copyPassword: '复制密码',
  copyCode: '复制验证码',
  copyApiKey: '复制密钥',
  copyToken: '复制令牌',
  copyCardNumber: '复制卡号',
  copyKey: '复制密钥',
  nothingToCopy: '此条目没有可复制的密码或密钥。',
  copied: '已复制',
  bulkFavorite: '置顶所选',
  bulkUnfavorite: '取消置顶',
  bulkTag: '添加标签',
  bulkTagPrompt: '要添加到所选条目的标签:',
  bulkFavorited: '已置顶 {n} 条。',
  bulkUnfavorited: '已取消置顶 {n} 条。',
  bulkTagged: '已为 {n} 条添加标签。',
  totp: '验证码',
  totpInlineHint: '当前一次性验证码, 每 30 秒自动刷新。点击 ⧉ 复制。',
  totpRowCopyHint: '点击验证码即可复制(每 30 秒自动刷新)',
  clone: '克隆',
  cloneSuffix: ' (副本)',
  clonedWithTitle: '已克隆为「{name}」。',
  searchResultsCount: '搜索: {n} 条匹配',
  previewImportTitle: 'CSV 导入预览',
  previewImportRows: '共 {n} 行, 跳过 {m} 行',
  confirmImport: '确认导入 {n} 条',
  attachFile: '添加附件',
  attachHint: '选择文件附加到此条目(加密存储)',
  downloadHint: '下载附件',
  attachmentNotFound: '附件不存在。',
  favOnly: '只看收藏',
  favOnlyHint: '仅显示已置顶(收藏)的条目',
  dueOnly: '仅看到期',
  dueOnlyHint: '仅显示已过期/临期/即将轮换的条目',
  issueOnly: '仅看问题',
  issueOnlyHint: '仅显示有健康问题的条目(弱密/重复/无2FA/HTTP)',
  copyAllFields: '复制全部字段',
  copyAllHint: '将条目所有字段复制为纯文本(每行一个字段)',
  density: '密度',
  densityHint: '切换列表密度(紧凑/舒适)',
  attachmentCountHint: '此条目含附件(显示数量)',
  bulkCopyTitles: '复制标题',
  vaultManageTitle: '保险库管理',
  vaultManageDesc: '当前保险库',

  vaultNew: '新建保险库',
  vaultNewPrompt: '新保险库名称(同名主密码的空库):',
  vaultNameInvalid: '名称无效 — 避免保留字 access/meta/-audit 等。',
  favorite: '★ 添加收藏',
  unfavorite: '☆ 取消收藏',
  favoriteAria: '添加收藏',
  unfavoriteAria: '取消收藏',
  favEmptyHint: '还没有收藏——点击条目标题旁的 ☆ 即可收藏。',
  fieldTitle: '标题',
  fieldKind: '类型',
  fieldUsername: '用户名',
  fieldEmail: '邮箱',
  fieldPhone: '手机号',
  fieldPassword: '密码',
  fieldHost: '主机',
  fieldPort: '端口',
  fieldApiKey: 'API 密钥',
  fieldSecret: 'Secret',
  fieldAccessToken: '访问令牌',
  fieldRefreshToken: '刷新令牌',
  fieldOtpSecret: 'TOTP 密钥',
  fieldUrl: 'URL',
  fieldCardNumber: '卡号',
  fieldCardExpiry: '有效期（MM/YY）',
  fieldCardCvv: 'CVV',
  fieldCardHolder: '持卡人',
  fieldNotes: '备注',
  fieldTags: '标签',
  fieldIcon: '图标（emoji）',
  fieldExpiresAt: '过期时间',
  fieldEnvKey: '环境变量名（可选）',
  fieldEnvKeyHint: 'vault_env 使用的确切环境变量名，逗号分隔且按顺序对应：第一个是主密钥，第二个是下一个（例如 DASHSCOPE_API_KEY，或 GOOGLE_ACCESS_TOKEN, GOOGLE_REFRESH_TOKEN）。留空则按标题自动推导。',
  fieldRotationDays: '轮换周期（天，0=永不）',
  fieldSensitivity: '敏感级别',
  fieldFavorite: '置顶',
  sensitivityNormal: '普通',
  sensitivityHigh: '高敏',
  favoriteHint: '列表置顶显示',
  rotationClearHint: '0 清除轮换',
  fieldColor: '颜色',
  fieldCustom: '自定义字段',
  fieldCreatedAt: '创建时间',
  fieldUpdatedAt: '更新时间',
  genLenChips: '快捷长度',
  riskWeakText: '⚠️ 密码强度较弱(建议 ≥12 位混合字符, 可用生成器)',
  riskReusedText: '⚠️ 此密码已被其他 {n} 个条目使用',
  urlLogoText: '将显示该站点图标',
  urlLogoHint: '列表中将显示该站点图标(除非你自定义了图标)。',
  customKeyPh: '键名',
  customValuePh: '值',
  addField: '+ 添加字段',
  customRemove: '删除此字段',
  error: '操作失败。',
  errLocked: '保险库已锁定，请先解锁。',
  errTitleEmpty: '标题不能为空。',
  errPathEmpty: '路径不能为空。',
  entryNotFound: '条目不存在。',
  errInvalidPort: '端口必须为 1-65535 的数字。',
  errInvalidVaultName: '保险库名称无效。',
  errReadonly: '当前为只读模式，此操作已禁用。',
  errDetail: '操作失败：{detail}',
  autoLockLabel: '自动锁定',
  autoLockNever: '永不',
  autoLock1m: '1 分钟',
  autoLock5m: '5 分钟',
  autoLock15m: '15 分钟',
  autoLock30m: '30 分钟',
  autoLock1h: '1 小时',
  autoLockHint: '自动锁定',
  autoLockNeverHint: '自动锁定已关闭',
  purge: '永久删除',
  purgeOneConfirm: '永久删除「{name}」？此操作不可撤销。',
  genPassphrase: '易记短语密码',
  genWords: '单词数（2–12）',
  genSeparator: '分隔符',
  genWordDigits: '尾部加 2 位数字',
  kindLogin: '登录',
  kindSsh: 'SSH',
  kindApiKey: 'API 密钥',
  kindSecret: 'Secret',
  kindOauth: 'OAuth',
  kindCookie: '会话 Cookies',
  kindCard: '银行卡',
  kindCustom: '自定义',
}
